import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { FIELD_META, SEARCH_FIELDS, Suggestion, getSuggestions } from '@/features/search/query';
import { useSearchVocab } from '@/features/search/use-vocab';
import { Member } from '@/features/backlog/types';
import { priorityMeta, taskTypeMeta } from '@/features/backlog/constants';
import { PriorityChevron, Search, Tag, TaskTypeGlyph, X } from '@/components/ui/icons';
import MemberAvatar from '@/components/member-avatar';
import { cn } from '@/components/ui/cn';

/** Lead visual for an autocomplete suggestion, by category. Shared with the global search flyout. */
export function SuggestionLead({ s, memberById }: { s: Suggestion; memberById: Map<string, Member> }) {
  if (s.category === 'field') return <Tag className="h-4 w-4 text-fg-faint" />;
  if (s.category === 'type' && s.value) return <TaskTypeGlyph type={s.value} color={taskTypeMeta(s.value).color} />;
  if (s.category === 'priority' && s.value) {
    const m = priorityMeta(s.value);
    return (
      <span className="flex h-4 w-4 items-center justify-center">
        <PriorityChevron level={m.level} color={m.color} />
      </span>
    );
  }
  if (s.category === 'assignee') {
    if (s.value === 'none' || !s.value) return <MemberAvatar member={undefined} size={18} />;
    return <MemberAvatar member={memberById.get(s.value)} size={18} />;
  }
  if (s.category === 'board') return <span className="flex h-4 w-4 items-center justify-center rounded bg-layer-strong text-[9px] font-bold text-fg-faint">B</span>;
  return <span className="h-2 w-2 rounded-full bg-fg-ghost" />;
}

/**
 * A single-line SparkQL query input with the SAME token-aware autocomplete as the global search:
 * type a field prefix (`assi…` → `assignee:`) then a value (boards / members / statuses / labels / types
 * / priorities), Tab/Enter/click to accept, arrow keys to navigate. When empty it offers the field-name
 * chips. It owns only the input + the suggestion flyout — the caller owns the query string and decides
 * what Enter (with no suggestion active) does via {@link onSubmit}.
 */
export default function SparkQLInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  autoFocus,
  max = 7,
}: {
  value: string;
  onChange: (v: string) => void;
  /** Enter with no suggestion highlighted. */
  onSubmit?: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  max?: number;
}) {
  const { boards, members } = useAppData();
  const { me } = useIdentity();
  const { labels, statusLabels } = useSearchVocab();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const memberById = useMemo(() => new Map(members.map((m) => [m.id, m])), [members]);
  const ctx = useMemo(() => ({ boards, members, meId: me?.id, labels, statusLabels }), [boards, members, me, labels, statusLabels]);

  const { replaceStart, suggestions } = useMemo(
    () => (open ? getSuggestions(value, ctx, max) : { replaceStart: 0, suggestions: [] as Suggestion[] }),
    [open, value, ctx, max],
  );
  // Suggestions show while typing a segment (not right after a space); when empty we show field chips.
  const showSuggestions = value.trim() !== '' && !/\s$/.test(value) && suggestions.length > 0;
  const showFields = open && value.trim() === '';

  useEffect(() => setActive(-1), [value, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const applySuggestion = useCallback(
    (s: Suggestion) => {
      onChange(value.slice(0, replaceStart) + s.insert);
      inputRef.current?.focus();
      setActive(-1);
    },
    [value, replaceStart, onChange],
  );

  const addField = useCallback(
    (f: string) => {
      onChange((value ? value.replace(/\s*$/, ' ') : '') + `${f}:`);
      inputRef.current?.focus();
      setOpen(true);
    },
    [value, onChange],
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      if (open) { e.stopPropagation(); setOpen(false); }
      return;
    }
    if (e.key === 'ArrowDown' && showSuggestions) {
      e.preventDefault();
      setOpen(true);
      setActive((a) => (a + 1) % suggestions.length);
      return;
    }
    if (e.key === 'ArrowUp' && showSuggestions) {
      e.preventDefault();
      setActive((a) => (a - 1 + suggestions.length) % suggestions.length);
      return;
    }
    if (e.key === 'Tab' && showSuggestions) {
      e.preventDefault();
      applySuggestion(suggestions[active >= 0 ? active : 0]);
      return;
    }
    if (e.key === 'Enter') {
      if (showSuggestions && active >= 0) {
        e.preventDefault();
        applySuggestion(suggestions[active]);
        return;
      }
      if (onSubmit) {
        e.preventDefault();
        onSubmit(value);
      }
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="flex h-10 items-center gap-2 rounded-xl border border-line bg-surface-inset px-3 focus-within:border-accent/50">
        <Search className="h-4 w-4 shrink-0 text-fg-faint" />
        <input
          ref={inputRef}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-[13.5px] text-fg placeholder:text-fg-ghost focus:outline-none"
        />
        {value ? (
          <button type="button" onClick={() => { onChange(''); inputRef.current?.focus(); }} aria-label="Clear" className="shrink-0 text-fg-faint transition-colors hover:text-fg-soft">
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      {open && (showSuggestions || showFields) ? (
        <div className="thin-scroll absolute left-0 right-0 top-[calc(100%+4px)] z-[1100] max-h-[45vh] overflow-auto rounded-xl border border-line bg-surface-raised p-1.5 shadow-2xl shadow-black/50">
          {showFields ? (
            <div>
              <div className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-fg-ghost">Filter by field</div>
              <div className="flex flex-wrap gap-1.5 px-2 pb-1 pt-1">
                {SEARCH_FIELDS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => addField(f)}
                    title={FIELD_META[f].hint}
                    className="rounded-full border border-line bg-layer px-2.5 py-1 font-mono text-[11.5px] text-fg-soft transition-colors hover:border-accent/40 hover:text-fg"
                  >
                    {f}:
                  </button>
                ))}
              </div>
            </div>
          ) : (
            suggestions.map((s, i) => (
              <button
                key={s.id}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => applySuggestion(s)}
                onMouseEnter={() => setActive(i)}
                className={cn('flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors', active === i ? 'bg-layer' : 'hover:bg-layer')}
              >
                <SuggestionLead s={s} memberById={memberById} />
                <span className="min-w-0 flex-1 truncate text-[13px] text-fg-soft">
                  {s.category === 'field' ? <span className="font-mono">{s.label}</span> : s.label}
                </span>
                {s.detail ? <span className="shrink-0 truncate text-[11px] text-fg-ghost">{s.detail}</span> : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
